import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { withImageLicensing } from "@/data/imageLicensing";
import { collectManufacturerPhotoSchemas } from "@/data/manufacturerPhotoSchemas";
import heroPreloadUrl from "@/assets/custom-staircase-matching-walnut-flooring-farmington-hills-mi.webp";

/**
 * Runtime LCP preload for the homepage hero image.
 *
 * In production, scripts/generate-seo-html.mjs rewrites the static preload
 * tag in index.html with the hashed asset URL during prerender. For dev and
 * any cold-start HTML that bypasses prerender, this runtime injection makes
 * sure the hero image still gets a hi-priority preload using the URL Vite
 * resolves at build time (hashed in prod, /src/... in dev).
 */
(() => {
  if (typeof document === "undefined") return;
  const existing = document.head.querySelector<HTMLLinkElement>(
    `link[rel="preload"][as="image"][href="${heroPreloadUrl}"]`
  );
  if (existing) return;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.type = "image/webp";
  link.setAttribute("fetchpriority", "high");
  link.href = heroPreloadUrl;
  document.head.appendChild(link);
})();

/**
 * Defer third-party analytics container scripts until after first paint.
 *
 * The synchronous gtag() shim in index.html has already queued
 * gtag('js', ...), gtag('config', 'G-432R4ZH0NP'), and
 * gtag('config', 'AW-1018637632') into window.dataLayer. Any further
 * gtag('event', ...) calls (page_view, generate_lead, phone_click,
 * Ads conversions) made before these containers hydrate will also queue
 * in dataLayer and replay automatically once the container loads — this
 * is the standard gtag.js behavior, so deferring is safe for Google Ads
 * conversion tracking on bounced sessions.
 */
const loadDeferredScripts = () => {
  const scripts: Array<{ src: string; attrs?: Record<string, string> }> = [
    { src: "https://www.googletagmanager.com/gtag/js?id=G-432R4ZH0NP" },
    { src: "https://www.googletagmanager.com/gtag/js?id=AW-1018637632" },
    {
      src: "https://analytics.ahrefs.com/analytics.js",
      attrs: { "data-key": "IWAl3jQWqfdXST7wGsOBoQ" },
    },
  ];
  for (const { src, attrs } of scripts) {
    if (document.querySelector(`script[src="${src}"]`)) continue;
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    if (attrs) for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v);
    document.head.appendChild(s);
  }
};

const scheduleDeferredScripts = () => {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(loadDeferredScripts, { timeout: 4000 });
  } else {
    // Safari fallback
    setTimeout(loadDeferredScripts, 2000);
  }
};

if (document.readyState === "complete") {
  scheduleDeferredScripts();
} else {
  window.addEventListener("load", scheduleDeferredScripts, { once: true });
}

// We intentionally use createRoot (not hydrateRoot) even when the build-time
// pre-render has filled #root with markup. The prerender HTML is captured
// AFTER lazy() chunks resolve and AFTER data-fetching effects run
// (useGoogleReviews, usePageSEO, etc.), so the DOM React would expect to
// hydrate against on a cold client load never matches the snapshot.
// That mismatch triggers React error #418 / #423 in production, which
// Lighthouse counts against Best Practices.
//
// The prerendered markup still serves its purpose: crawlers (Googlebot,
// social previews) see fully-rendered content immediately, and first paint
// for users is the prerendered HTML. createRoot then cleanly takes over,
// replacing the static markup with the live React tree without hydration
// warnings.
// Every JSON-LD block (static or injected later) gets ImageObject licensing and credit fields.
(() => {
  const normalize = (el: HTMLScriptElement) => {
    try {
      const text = el.textContent || "";
      if (!text.includes("ImageObject")) return;
      const next = JSON.stringify(withImageLicensing(JSON.parse(text), window.location.pathname));
      if (next !== text) el.textContent = next;
    } catch { /* leave malformed blocks untouched */ }
  };
  const scan = () => document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]').forEach(normalize);
  scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
})();

// Follow mounted brand photos and filters without changing their markup.
(() => {
  const sync = () => {
    const records = collectManufacturerPhotoSchemas(document, window.location.pathname);
    const existing = document.getElementById('manufacturer-photo-schemas');
    if (!records.length) { existing?.remove(); return; }
    const text = JSON.stringify(records);
    if (existing?.textContent === text) return;
    const script = existing || document.createElement('script');
    script.id = 'manufacturer-photo-schemas';
    script.setAttribute('type', 'application/ld+json');
    script.textContent = text;
    if (!existing) document.head.appendChild(script);
  };
  new MutationObserver(sync).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'alt', 'data-route'],
  });
  sync();
})();

const container = document.getElementById("root")!;
createRoot(container).render(<App />);
